import { useState, useEffect, useMemo } from "react";
import { Card, Row, Col, Typography, Avatar, Statistic, Space } from "antd";
import {
  IoCall,
  IoCallOutline,
  IoTrendingUp,
  IoTrendingDown,
  IoAlertCircle,
} from "react-icons/io5";
import ReactApexChart from "react-apexcharts";
import { useSelector, useDispatch } from "react-redux";
import "./Dashboard.css";
import DateRangePicker from "react-bootstrap-daterangepicker";
import api from "../../../core/axios/axiosInstance";
import dayjs from "dayjs";
import {
  fetchCallGraph,
  fetchDashboardCallHistory,
} from "../../../core/data/redux/slices/DashboardDataSlice";

const { Title, Text } = Typography;

const Dashboard = () => {
  const dispatch = useDispatch();
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const last7Start = new Date(today);
  last7Start.setDate(today.getDate() - 7);

  const last30Start = new Date(today);
  last30Start.setDate(today.getDate() - 30);

  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const thisMonthEnd = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0,
    23,
    59,
    59,
    999
  );

  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthEnd = new Date(
    now.getFullYear(),
    now.getMonth(),
    0,
    23,
    59,
    59,
    999
  );
  const profile = useSelector((state) => state.profile);

  const { callHistory, callGraph } = useSelector(
    (state) => state.dashboardData
  );
  // console.log(callHistory, "callhistoryredux");
  // console.log(callGraph, "callGraphredux");

  const userInfo = {
    name:
      profile.firstname && profile.lastname
        ? `${profile.firstname} ${profile.lastname}`
        : profile.email || "User",
    firstname: profile.firstname || "",
    lastname: profile.lastname || "",
    email: profile.email || "",
    avatar: profile.profileImageURL || null,
    yeastarExtensionId: profile.yeastarExtensionId,
    extensionNumber: profile.extensionNumber,
    role: profile.role || "User",
  };

  const [startDate, setStartDate] = useState(
    dayjs(last7Start).format("YYYY-MM-DD")
  );

  const [endDate, setEndDate] = useState(dayjs(today).format("YYYY-MM-DD"));

  // Chart configurations
  const lineChartOptions = {
    chart: {
      type: "line",
      height: 350,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    colors: ["#52c41a", "#1890ff"],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 3,
    },
    grid: {
      row: {
        colors: ["#f3f3f3", "transparent"],
        opacity: 0.5,
      },
    },
    xaxis: {
      categories: callGraph?.days?.map((item) => `${item.date}`),
      title: {
        text: "Date",
      },
    },
    yaxis: {
      title: {
        text: "Number of Calls",
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: 250,
          },
        },
      },
    ],
  };

  useEffect(() => {
    dispatch(fetchDashboardCallHistory({ startDate, endDate }));
    if (!callGraph?.days?.length) {
      dispatch(fetchCallGraph());
    }
  }, [startDate, endDate]);

  const lineChartSeries = [
    {
      name: "Inbound Calls",
      data: callGraph?.days?.map((item) => item.inbound),
    },
    {
      name: "Outbound Calls",
      data: callGraph?.days?.map((item) => item.outbound),
    },
  ];

  const initialSettings = {
    ranges: {
      "Last 30 Days": [last30Start, now],
      "Last 7 Days": [last7Start, now],
      "Last Month": [lastMonthStart, lastMonthEnd],
      "This Month": [thisMonthStart, thisMonthEnd],
      Today: [today, today],
      Yesterday: [yesterday, yesterday],
    },

    startDate: dayjs(last7Start).toDate(),
    endDate: dayjs(today).toDate(),
    timePicker: false,
    locale: {
      format: "YYYY-MM-DD",
    },
  };

  const pieChartSeries = [
    callHistory?.summary?.answeredTotal ?? 0,
    callHistory?.summary?.missedTotal ?? 0,
    callHistory?.summary?.cancelledTotal ?? 0,
    callHistory?.summary?.invalidTotal ?? 0,
  ];
  const hasData = pieChartSeries.some((value) => value > 0);

  // if no data → show a single fake slice
  const finalSeries = hasData ? pieChartSeries : [1];
  const pieChartOptions = useMemo(
    () => ({
      chart: {
        type: "donut",
        height: 350,
      },

      colors: hasData
        ? ["#52c41a", "#ff4d4f", "#faad14", "#14a6fa"]
        : ["#d9d9d9"],

      labels: hasData
        ? ["Answered Calls", "Missed Calls", "Cancelled Calls", "Invalid Calls"]
        : ["No Data"],

      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              total: {
                show: true,
                label: hasData ? "Total" : "No Data",
                formatter: () =>
                  hasData ? `${callHistory?.summary?.totalCalls}` : "",
                fontSize: "16px",
                fontFamily: "inherit",
              },
            },
          },
        },
      },

      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              height: 250,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
    }),
    [callHistory]
  );

  const currentDate = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const currentTime = new Date().toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-header">
                  {/* Header Section */}
                  <div className="row align-items-center">
                    <div className="col-sm-12">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="page-header mb-0">
                          <div className="row align-items-center">
                            <h4 className="page-title mb-0">Dashboard</h4>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <div className="user-dashboard">
                    {/* Data Cards Row */}
                    <Row gutter={[34, 34]} className="data-cards-row mb-4">
                      <Col xs={24} sm={24} lg={24}>
                        <div className="d-flex align-items-end justify-content-between">
                          <Space size="middle" align="start">
                            <Avatar
                              size={48}
                              style={{
                                backgroundColor: "#1890ff",
                                fontSize: "18px",
                                fontWeight: "bold",
                              }}
                              src={userInfo.avatar}
                            >
                              {userInfo.name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                                .toUpperCase()}
                            </Avatar>
                            <div className="user-info">
                              <Text className="welcome-text">
                                Welcome back,{" "}
                                {userInfo.firstname || userInfo.name}!
                              </Text>

                              <Text
                                type="secondary"
                                style={{
                                  fontSize: "14px",
                                  marginBottom: "5px",
                                }}
                              >
                                Extension Number:{" "}
                                <span className=" fw-bold text-purple">
                                  {userInfo.extensionNumber}
                                </span>
                              </Text>
                              <Text
                                type="secondary"
                                style={{
                                  fontSize: "14px",
                                  marginBottom: "5px",
                                }}
                              >
                                Telephone Number:{" "}
                                <span className=" fw-bold text-purple">
                                  +{profile.telephone}
                                </span>
                              </Text>
                              <Text
                                type="secondary"
                                style={{
                                  fontSize: "14px",
                                  marginBottom: "5px",
                                }}
                              >
                                Extension Status:{" "}
                                <span className=" fw-bold text-purple">
                                  {profile.extensionStatus == true
                                    ? "Active"
                                    : "Disabled"}
                                </span>
                              </Text>
                              <Text
                                type="secondary"
                                style={{
                                  fontSize: "14px",
                                  marginBottom: "5px",
                                }}
                              >
                                Account Status:{" "}
                                <span className=" fw-bold text-purple">
                                  {profile.accountStatus == true
                                    ? "Active"
                                    : "Disabled"}
                                </span>
                              </Text>
                              <Text type="secondary" className="current-date">
                                {currentDate} • {currentTime}
                              </Text>
                            </div>
                          </Space>
                          <div className="icon-form me-2">
                            <span className="form-icon">
                              <i className="ti ti-calendar" />
                            </span>
                            <DateRangePicker
                              initialSettings={initialSettings}
                              onCallback={(start, end) => {
                                const startDate =
                                  dayjs(start).format("YYYY-MM-DD");
                                const endDate = dayjs(end).format("YYYY-MM-DD");

                                setStartDate(startDate);
                                setEndDate(endDate);
                              }}
                            >
                              <input
                                className="form-control bookingrange"
                                type="text"
                              />
                            </DateRangePicker>
                          </div>
                        </div>
                      </Col>
                    </Row>
                    <Row gutter={[24, 24]} className="data-cards-row mb-4">
                      <Col xs={24} sm={12} lg={6}>
                        <Card
                          className="metric-card inbound-card"
                          hoverable
                          styles={{
                            body: { padding: "24px" },
                          }}
                        >
                          <Space
                            direction="vertical"
                            size="small"
                            style={{ width: "100%" }}
                          >
                            <div className="metric-header justify-content-between align-items-center">
                              <div>
                                <IoCall
                                  size={24}
                                  className="metric-icon inbound-icon"
                                />
                                <Text
                                  type="secondary"
                                  className="metric-label ms-3"
                                >
                                  Inbound Calls
                                </Text>
                              </div>
                              <Statistic
                                value={callHistory?.summary?.inboundTotal}
                                precision={0}
                                className="metric-value"
                                valueStyle={{
                                  color: "#52c41a",
                                  fontSize: "28px",
                                }}
                              />
                            </div>

                            {/* <div className="metric-change">
                              <IoTrendingUp
                                size={16}
                                className="trend-icon positive"
                              />
                              <Text className="change-text positive">
                                +{callHistory.inboundChange}% from last month
                              </Text>
                            </div> */}
                          </Space>
                        </Card>
                      </Col>

                      <Col xs={24} sm={12} lg={6}>
                        <Card
                          className="metric-card outbound-card"
                          hoverable
                          styles={{
                            body: { padding: "24px" },
                          }}
                        >
                          <Space
                            direction="vertical"
                            size="small"
                            style={{ width: "100%" }}
                          >
                            <div className="metric-header justify-content-between align-items-center">
                              <div>
                                <IoCallOutline
                                  size={24}
                                  className="metric-icon outbound-icon"
                                />
                                <Text
                                  type="secondary"
                                  className="metric-label ms-3"
                                >
                                  Outbound Calls
                                </Text>
                              </div>

                              <Statistic
                                value={callHistory?.summary?.outboundTotal}
                                precision={0}
                                className="metric-value"
                                valueStyle={{
                                  color: "#1890ff",
                                  fontSize: "28px",
                                }}
                              />
                            </div>

                            {/* <div className="metric-change">
                              <IoTrendingDown
                                size={16}
                                className="trend-icon negative"
                              />
                              <Text className="change-text negative">
                                {callHistory.outboundChange}% from last month
                              </Text>
                            </div> */}
                          </Space>
                        </Card>
                      </Col>

                      <Col xs={24} sm={12} lg={6}>
                        <Card
                          className="metric-card credits-card"
                          hoverable
                          styles={{
                            body: { padding: "24px" },
                          }}
                        >
                          <Space
                            direction="vertical"
                            size="small"
                            style={{ width: "100%" }}
                          >
                            <div className="metric-header justify-content-between align-items-center">
                              <div>
                                <IoCall
                                  size={24}
                                  className="metric-icon missed-icon"
                                />
                                <Text
                                  type="secondary"
                                  className="metric-label ms-3"
                                >
                                  Missed Calls
                                </Text>
                              </div>

                              <Statistic
                                value={callHistory?.summary?.missedTotal}
                                precision={0}
                                className="metric-value"
                                valueStyle={{
                                  color: "#ff1818ff",
                                  fontSize: "28px",
                                }}
                              />
                            </div>
                            {/* <div className="metric-change">
                              <IoTrendingDown
                                size={16}
                                className="trend-icon negative"
                              />
                              <Text className="change-text negative">
                                {callHistory.outboundChange}% from last month
                              </Text>
                            </div> */}
                          </Space>
                        </Card>
                      </Col>

                      <Col xs={24} sm={12} lg={6}>
                        <Card
                          className="metric-card total-card"
                          hoverable
                          styles={{
                            body: { padding: "24px" },
                          }}
                        >
                          <Space
                            direction="vertical"
                            size="small"
                            style={{ width: "100%" }}
                          >
                            <div className="metric-header justify-content-between align-items-center">
                              <div>
                                <IoAlertCircle
                                  size={24}
                                  className="metric-icon total-icon"
                                />
                                <Text
                                  type="secondary"
                                  className="metric-label ms-3"
                                >
                                  Total Calls
                                </Text>
                              </div>
                              <Statistic
                                value={callHistory?.summary?.totalCalls}
                                precision={0}
                                className="metric-value"
                                valueStyle={{
                                  color: "#722ed1",
                                  fontSize: "28px",
                                }}
                              />
                            </div>
                            {/* <div className="metric-change">
                              <IoTrendingUp
                                size={16}
                                className="trend-icon positive"
                              />
                              <Text className="change-text positive">
                                +
                                {Math.round(
                                  (callHistory.inboundChange +
                                    Math.abs(callHistory.outboundChange)) /
                                  2
                                )}
                                % overall growth
                              </Text>
                            </div> */}
                          </Space>
                        </Card>
                      </Col>
                    </Row>

                    {/* Charts Section */}
                    <Row gutter={[24, 24]} className="charts-section">
                      {/* Daily Calls Over Time */}
                      <Col xs={24} lg={16}>
                        <Card
                          title="Last 30 Days Calls"
                          className="chart-card"
                          // extra={
                          //   <Text type="secondary" className="chart-period">
                          //     October 2024
                          //   </Text>
                          // }
                        >
                          <ReactApexChart
                            options={lineChartOptions}
                            series={lineChartSeries}
                            type="line"
                            height={350}
                          />
                        </Card>
                      </Col>

                      {/* Call Distribution */}
                      <Col xs={24} lg={8}>
                        <Card title="Call Success Rate" className="chart-card">
                          <ReactApexChart
                            key={callHistory?.summary?.totalCalls}
                            options={pieChartOptions}
                            series={finalSeries}
                            type="donut"
                            height={350}
                          />
                        </Card>
                      </Col>

                      {/* Credits Usage Over Time */}
                      {/* <Col xs={24}>
                        <Card
                          title="Credits Usage Trend"
                          className="chart-card"
                        >
                          <ReactApexChart
                            options={areaChartOptions}
                            series={areaChartSeries}
                            type="area"
                            height={300}
                          />
                        </Card>
                      </Col> */}
                    </Row>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
